'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { isElectron, gateway, chat as chatApi } from '@/lib/api';
import { estimateCost, formatDuration, formatModel, formatReset, formatTokens, modelFamilyRank } from '@/lib/model-format';

interface SessionInfo {
  model: { provider: string; id: string } | null;
  thinkingLevel: string;
  contextUsage: { tokens: number; contextWindow: number; percent: number };
  messageCount: number;
  isStreaming: boolean;
  autoCompact: boolean;
  compactions: number;
}

interface RateLimitBucket {
  used: number;
  limit: number;
  percent: number;
  resetsAt?: string;
}

interface RateLimitData {
  session?: RateLimitBucket;
  weekAll?: RateLimitBucket;
  weekSonnet?: RateLimitBucket;
  updatedAt: number;
}

interface ModelOption {
  provider: string;
  id: string;
  name: string;
  contextWindow: number;
  reasoning: boolean;
}

export function StatusBar() {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [startTime] = useState(Date.now());
  const [elapsed, setElapsed] = useState(0);
  const [cumulativeUsage, setCumulativeUsage] = useState({ input: 0, output: 0, cacheRead: 0, cacheWrite: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [showReset, setShowReset] = useState<'5h' | '7d' | null>(null);
  const [rateLimit, setRateLimit] = useState<RateLimitData | null>(null);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [switching, setSwitching] = useState(false);
  const [keepaliveCount, setKeepaliveCount] = useState(0);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const modelPickerRef = useRef<HTMLDivElement>(null);

  // Subscribe to gateway events stream for real-time soma notifications
  useEffect(() => {
    if (isElectron) {
      // Electron: IPC events
      const unsubscribe = chatApi.onEvent((data: any) => {
        if (data.type === 'connected') {
          setKeepaliveCount(data.keepaliveCount || 0);
        } else if (data.type === 'keepalive') {
          setKeepaliveCount(data.count);
          window.dispatchEvent(new CustomEvent('beast:keepalive', { detail: data }));
        } else if (data.type === 'keepalive_response') {
          window.dispatchEvent(new CustomEvent('beast:keepalive_response', { detail: data }));
        }
      });
      return unsubscribe;
    } else {
      // Browser: SSE EventSource
      const GATEWAY_URL = typeof window === 'undefined'
        ? (process.env.NEXT_PUBLIC_GATEWAY_URL || 'http://localhost:18790')
        : '/api/gw';
      let es: EventSource;
      let disposed = false;
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
      const connect = () => {
        if (disposed) return;
        es = new EventSource(`${GATEWAY_URL}/events`);
        es.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.type === 'connected') {
              setKeepaliveCount(data.keepaliveCount || 0);
            } else if (data.type === 'keepalive') {
              setKeepaliveCount(data.count);
              window.dispatchEvent(new CustomEvent('beast:keepalive', { detail: data }));
            } else if (data.type === 'keepalive_response') {
              window.dispatchEvent(new CustomEvent('beast:keepalive_response', { detail: data }));
            }
          } catch {}
        };
        es.onerror = () => {
          es.close();
          if (!disposed) reconnectTimer = setTimeout(connect, 5000);
        };
      };
      connect();
      return () => {
        disposed = true;
        if (reconnectTimer) clearTimeout(reconnectTimer);
        es?.close();
      };
    }
  }, []);

  const poll = useCallback(() => {
    gateway.session()
      .then((data: any) => { if (data) setSession(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    poll();
    const interval = setInterval(poll, 5_000);
    return () => clearInterval(interval);
  }, [poll]);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  useEffect(() => {
    const handler = (e: CustomEvent) => {
      const u = e.detail;
      if (u) {
        setCumulativeUsage(prev => ({
          input: prev.input + (u.input || 0),
          output: prev.output + (u.output || 0),
          cacheRead: prev.cacheRead + (u.cacheRead || 0),
          cacheWrite: prev.cacheWrite + (u.cacheWrite || 0),
        }));
      }
    };
    window.addEventListener('beast:usage' as any, handler as any);
    return () => window.removeEventListener('beast:usage' as any, handler as any);
  }, []);

  // Fetch rate limit data
  const fetchUsage = useCallback(() => {
    gateway.usage()
      .then((data: any) => { if (data && data.updatedAt > 0) setRateLimit(data); })
      .catch(() => {});
  }, []);

  // Listen for /usage command from chat
  useEffect(() => {
    const handler = () => fetchUsage();
    window.addEventListener('beast:fetch-usage' as any, handler);
    return () => window.removeEventListener('beast:fetch-usage' as any, handler);
  }, [fetchUsage]);

  // Also fetch usage on every turn completion (headers get updated with each API call)
  useEffect(() => {
    const handler = () => fetchUsage();
    window.addEventListener('beast:usage' as any, handler);
    return () => window.removeEventListener('beast:usage' as any, handler);
  }, [fetchUsage]);

  // Periodic rate limit refresh — keeps 5h/7d display current between messages
  useEffect(() => {
    fetchUsage();
    const interval = setInterval(fetchUsage, 60_000);
    return () => clearInterval(interval);
  }, [fetchUsage]);

  // Fetch available models when picker opens
  useEffect(() => {
    if (!showModelPicker) return;
    gateway.models()
      .then((data: any) => { if (data.models) setModels(data.models); })
      .catch(() => {});
  }, [showModelPicker]);

  const switchModel = useCallback(async (provider: string, id: string) => {
    setSwitching(true);
    try {
      const data = await gateway.modelSwitch(provider, id);
      if (data.ok) {
        poll();
        setShowModelPicker(false);
      }
    } catch {}
    setSwitching(false);
  }, [poll]);

  // Close tooltip/picker on click outside
  useEffect(() => {
    if (!showTooltip && !showModelPicker) return;
    const handler = (e: MouseEvent) => {
      if (showTooltip && tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setShowTooltip(false);
      }
      if (showModelPicker && modelPickerRef.current && !modelPickerRef.current.contains(e.target as Node)) {
        setShowModelPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showTooltip, showModelPicker]);

  if (!session) return null;

  const model = session.model ? formatModel(session.model.id) : '—';
  const thinking = session.thinkingLevel || '—';
  const contextPct = Math.round(session.contextUsage.percent);
  const compactions = session.compactions ?? 0;
  const cost = estimateCost(cumulativeUsage, session.model?.id);
  const msgs = session.messageCount;
  const u = cumulativeUsage;

  return (
    <div className="flex items-center gap-1.5 px-1 text-[10px] font-mono leading-none flex-wrap">
      <div className="relative inline-flex" ref={modelPickerRef}>
        <button
          type="button"
          onClick={() => { setShowModelPicker(!showModelPicker); setShowTooltip(false); }}
          className="text-cyan-400 font-semibold hover:text-cyan-300 transition-colors cursor-pointer"
          title="Click to switch model"
        >
          {model}
        </button>
        {showModelPicker && (
          <div className="absolute bottom-full left-0 mb-2 w-72 bg-gray-900 border border-[rgb(var(--border))]/60 rounded-lg shadow-xl shadow-black/40 z-50 max-h-[50vh] overflow-y-auto">
            <div className="px-3 py-2 border-b border-[rgb(var(--border))]/40">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">Switch Model</div>
            </div>
            {models.length === 0 ? (
              <div className="px-3 py-4 text-[11px] text-gray-600 text-center">Loading…</div>
            ) : (
              <div className="py-1">
                {models
                  .filter(m => {
                    // Keep only clean model names — skip dated duplicates and 'latest' aliases
                    if (m.id.includes('latest')) return false;
                    // Keep dated versions only if there's no clean alias
                    // e.g. keep "claude-sonnet-4-5" but skip "claude-sonnet-4-5-20250929"
                    if (m.id.match(/-\d{8}$/)) {
                      const base = m.id.replace(/-\d{8}$/, '');
                      return !models.some(other => other.id === base);
                    }
                    return true;
                  })
                  .sort((a, b) => {
                    // Sort: current first, then by family (fable/opus/sonnet/…), then version desc
                    const aCurrent = session?.model?.id === a.id ? -1 : 0;
                    const bCurrent = session?.model?.id === b.id ? -1 : 0;
                    if (aCurrent !== bCurrent) return aCurrent - bCurrent;
                    const aFamily = modelFamilyRank(a.id);
                    const bFamily = modelFamilyRank(b.id);
                    if (aFamily !== bFamily) return aFamily - bFamily;
                    return b.id.localeCompare(a.id); // newest first within family
                  })
                  .map(m => {
                    const isCurrent = session?.model?.id === m.id;
                    const shortName = formatModel(m.id);
                    const ctx = m.contextWindow >= 1_000_000 ? '1M' : `${Math.round(m.contextWindow / 1000)}k`;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        disabled={switching || isCurrent}
                        onClick={() => switchModel(m.provider, m.id)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
                          isCurrent
                            ? 'bg-cyan-500/10 border-l-2 border-cyan-400'
                            : 'border-l-2 border-transparent hover:bg-[rgb(var(--muted))]/50'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className={`text-[11px] font-mono ${isCurrent ? 'text-cyan-300' : 'text-gray-300'}`}>
                            {shortName}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {m.reasoning && (
                            <span className="text-[9px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-300">think</span>
                          )}
                          <span className="text-[9px] text-gray-600">{ctx}</span>
                        </div>
                      </button>
                    );
                  })}
              </div>
            )}
            {switching && (
              <div className="px-3 py-2 border-t border-[rgb(var(--border))]/40 text-[10px] text-cyan-400 text-center">
                Switching…
              </div>
            )}
          </div>
        )}
      </div>
      <span className="text-gray-700">•</span>
      <span className="text-gray-500">{thinking}</span>
      <span className="text-gray-700">─</span>
      <span className={`${contextPct > 90 ? 'text-red-400 animate-pulse' : contextPct > 70 ? 'text-amber-400' : 'text-emerald-400'}`}>
        ◉{contextPct}%
      </span>
      {contextPct > 70 && (
        <span className="text-[9px] text-amber-400/70">breathe</span>
      )}
      <span className="text-gray-700">─</span>

      {/* Cost with token breakdown tooltip */}
      <div className="relative inline-flex" ref={tooltipRef}>
        <button
          type="button"
          onClick={() => setShowTooltip(!showTooltip)}
          className="text-green-500/70 hover:text-green-400 transition-colors cursor-pointer"
        >
          ${cost.toFixed(2)}
        </button>
        {showTooltip && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-[rgb(var(--border))]/60 rounded-lg shadow-xl shadow-black/40 whitespace-nowrap z-50">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Token Usage</div>
            <div className="space-y-1 text-[11px]">
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">In</span>
                <span className="text-gray-300">{formatTokens(u.input)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Out</span>
                <span className="text-gray-300">{formatTokens(u.output)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Cache read</span>
                <span className="text-gray-300">{formatTokens(u.cacheRead)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Cache write</span>
                <span className="text-gray-300">{formatTokens(u.cacheWrite)}</span>
              </div>
              <div className="border-t border-[rgb(var(--border))]/40 pt-1 flex justify-between gap-4">
                <span className="text-gray-400">Total</span>
                <span className="text-green-400 font-semibold">{formatTokens(u.input + u.output + u.cacheRead + u.cacheWrite)}</span>
              </div>
            </div>
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-gray-700/60" />
          </div>
        )}
      </div>

      <span className="text-gray-700">─</span>
      <span className="text-gray-600">◷</span>
      <span className="text-gray-400">{formatDuration(elapsed)}</span>
      <span className="text-gray-700">─</span>
      <span className="text-pink-400/70" title="Cache keepalives">
        {keepaliveCount > 0 ? `♥${keepaliveCount}` : '♥on'}
      </span>
      <span className="text-gray-700">─</span>
      <span className="text-gray-600">¶</span>
      <span className="text-gray-400">{msgs}</span>
      {session.isStreaming && (
        <>
          <span className="text-gray-700">─</span>
          <span className="text-cyan-400 animate-pulse">●</span>
        </>
      )}
      {rateLimit && rateLimit.updatedAt > 0 && (
        <>
          <span className="text-gray-700 ml-1">│</span>
          {rateLimit.session && (
            <button
              type="button"
              onClick={() => setShowReset(showReset === '5h' ? null : '5h')}
              className={`${rateLimit.session.percent > 80 ? 'text-red-400' : rateLimit.session.percent > 50 ? 'text-amber-400' : 'text-gray-400'} hover:text-white transition-colors cursor-pointer`}
            >
              {showReset === '5h' && rateLimit.session.resetsAt
                ? `5h:${formatReset(rateLimit.session.resetsAt)}`
                : `5h:${rateLimit.session.percent}%`}
            </button>
          )}
          {rateLimit.weekAll && (
            <button
              type="button"
              onClick={() => setShowReset(showReset === '7d' ? null : '7d')}
              className={`${rateLimit.weekAll.percent > 80 ? 'text-red-400' : rateLimit.weekAll.percent > 50 ? 'text-amber-400' : 'text-gray-400'} hover:text-white transition-colors cursor-pointer`}
            >
              {showReset === '7d' && rateLimit.weekAll.resetsAt
                ? `7d:${formatReset(rateLimit.weekAll.resetsAt)}`
                : `7d:${rateLimit.weekAll.percent}%`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
